import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import SettingsIcon from "@mui/icons-material/Settings";
import SpeedIcon from "@mui/icons-material/Speed";
import StorageIcon from "@mui/icons-material/Storage";
import TableRowsIcon from "@mui/icons-material/TableRows";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import type {
  DatasourceDto,
  DatabaseCollectionDto,
} from "../lib/generated/api-client";
import { DatasourceDtoTypeEnum } from "../lib/generated/api-client";
import { useDatasources } from "../lib/useDatasources";
import { navDashboards } from "../lib/nav-dashboards";
import {
  latestSuccessAttempt,
  latestSuccessCollection,
  pickDemoDatasource,
} from "../lib/after-auth";
import {
  isSampleVisible,
  visibleDatasources,
} from "../lib/demo-preference";
import { datasourceDisplayName } from "../lib/sample-label";
import { useThemeMode } from "../lib/theme-mode";
import { useAuth } from "../lib/useAuth";
import { visibleCollections } from "../lib/member-access";
import {
  collectionsFromPreview,
  datasourceIdFromPath,
  hrefWithPreview,
  previewIdFromLocation,
  tabRowCountLabel,
} from "../lib/preview-as";
import { usePreviewSession } from "../lib/preview-session";
import { humanize } from "../lib/schema-field";
import { Button } from "./ui/Button";

// Helper function to determine active states
const useNavigationState = () => {
  const location = useLocation();
  
  // Parse URL manually since Nav is outside Routes and useParams() doesn't work
  // URL format: /datasources/:datasourceId/collections/:collectionName
  // or: /datasources/:datasourceId/dashboards/:id
  const pathParts = location.pathname.split('/').filter(Boolean);
  
  let currentDatasourceId: string | undefined;
  let currentCollectionName: string | undefined;
  let currentDashboardId: string | undefined;
  
  if (pathParts[0] === 'datasources' && pathParts[1]) {
    currentDatasourceId = pathParts[1];
    
    if (pathParts[2] === 'collections' && pathParts[3]) {
      currentCollectionName = decodeURIComponent(pathParts[3]);
    } else if (pathParts[2] === 'dashboards' && pathParts[3]) {
      currentDashboardId = pathParts[3];
    }
  }

  const isDataSourcesPage = (location.pathname === "/data-sources" || location.pathname === "/") && 
                             !currentDatasourceId;
  const isSettingsPage = location.pathname === "/settings";
  
  return {
    isDataSourcesPage,
    isSettingsPage,
    currentDatasourceId,
    currentCollectionName,
    currentDashboardId,
  };
};

/**
 * A schema with sixty tables is unusable as a flat list, so anything past a
 * dozen gets a filter box. Names are humanised to match the column headers and
 * page titles, with the raw table name kept in the tooltip.
 */
const CollectionList: React.FC<{
  heading: string;
  datasourceId: string;
  collections: Array<DatabaseCollectionDto & { previewRowCount?: number }>;
  activeCollectionName?: string;
  previewMemberId?: string | null;
}> = ({
  heading,
  datasourceId,
  collections,
  activeCollectionName,
  previewMemberId = null,
}) => {
  const [filter, setFilter] = useState("");
  const activeLinkRef = useCallback((link: HTMLAnchorElement | null) => {
    link?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, []);
  const showFilter = collections.length > 12;

  // `information_schema` order is arbitrary, which in a 43-table schema means a
  // list nobody can scan. Sort by the label that is actually rendered.
  const sorted = useMemo(
    () =>
      [...collections].sort((a, b) =>
        humanize(a.name).localeCompare(humanize(b.name))
      ),
    [collections]
  );

  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return sorted;
    return sorted.filter((c) => c.name.toLowerCase().includes(needle));
  }, [sorted, filter]);

  return (
    <div className="mt-2 ml-1.5">
      <div className="flex items-baseline justify-between px-2 mb-1">
        <h3 className="text-xs font-semibold text-od-muted uppercase tracking-wider">
          {heading}
        </h3>
        <span className="text-xs text-od-muted/70">
          {showFilter && filter ? `${visible.length}/${collections.length}` : collections.length}
        </span>
      </div>

      {showFilter && (
        <input
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter tables…"
          aria-label={`Filter ${heading.toLowerCase()}`}
          className="mb-1.5 h-7 w-full rounded-md border border-od-border bg-od-surface px-2 text-xs text-od-text outline-none transition-colors placeholder:text-od-muted focus:border-od-primary"
        />
      )}

      <div className="space-y-0.5">
        {visible.map((collection) => {
          const isActive = activeCollectionName === collection.name;
          return (
            <Link
              key={collection.id}
              ref={isActive ? activeLinkRef : undefined}
              to={hrefWithPreview(
                `/datasources/${datasourceId}/collections/${collection.name}`,
                previewMemberId,
              )}
              title={collection.name}
              className={`flex items-center px-2.5 py-1.5 rounded-md text-xs transition-colors duration-150 group relative ${
                isActive
                  ? "bg-od-primary/10 text-od-primary-text border border-od-primary/30 font-semibold"
                  : "text-od-muted hover:text-od-text hover:bg-od-surface-2"
              }`}
            >
              {isActive && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-4 bg-od-primary rounded-r"></div>
              )}
              <TableRowsIcon
                sx={{ fontSize: 13 }}
                className={`mr-2 ${
                  isActive ? "text-od-primary-text" : "text-od-muted/60 group-hover:text-od-muted"
                }`}
              />
              <span className="truncate">{humanize(collection.name)}</span>
              {typeof collection.previewRowCount === "number" && (
                <span className="ml-auto shrink-0 pl-2 text-od-muted">
                  {tabRowCountLabel(collection.previewRowCount)}
                </span>
              )}
            </Link>
          );
        })}

        {showFilter && visible.length === 0 && (
          <p className="px-2.5 py-2 text-xs text-od-muted">
            No table matches “{filter}”.
          </p>
        )}
      </div>
    </div>
  );
};

const Nav = () => {
  const { datasources } = useDatasources();
  const { resolvedMode, toggleMode } = useThemeMode();
  const { user, guest, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const preview = usePreviewSession();
  const urlPreviewId = previewIdFromLocation(location);
  const pathDatasourceId = datasourceIdFromPath(location.pathname);
  const navState = useNavigationState();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const accountId =
    (typeof user?.id === "string" && user.id) ||
    (typeof guest?.id === "string" && guest.id) ||
    null;
  const [sampleVisible, setSampleVisibleState] = useState(() =>
    isSampleVisible(accountId),
  );
  const guestLabel =
    typeof guest?.name === "string" ? guest.name.trim() : "";
  const showGuestChip = Boolean(guest) && !user?.email && !guestLabel;
  // An email and a Sign out button do not both fit on one 288 px line, so the
  // identity takes its own row and keeps the full value in the tooltip.
  const accountName = [user?.firstName, user?.lastName]
    .filter((part): part is string => typeof part === "string" && !!part.trim())
    .join(" ");
  const accountLabel = user?.email || guestLabel || "Guest User";
  const accountTitle = accountName
    ? `${accountLabel} (${accountName})`
    : accountLabel;
  
  // Persist collapsed state in localStorage
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem("nav-collapsed");
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    const sync = () => setSampleVisibleState(isSampleVisible(accountId));
    sync();
    window.addEventListener("od:sample-visible-change", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("od:sample-visible-change", sync);
      window.removeEventListener("storage", sync);
    };
  }, [accountId]);

  // Guests who opened the demo only see that sample. Signed-in users see own
  // sources, plus the sample only when opted in (or when a direct demo link is
  // the current route).
  const canonicalDemo = pickDemoDatasource(datasources);
  const navigationDatasources = guest
    ? canonicalDemo
      ? [canonicalDemo]
      : []
    : visibleDatasources(datasources, {
        showSample: sampleVisible,
        viewingDatasourceId: navState.currentDatasourceId,
      });

  // Guest navigation and its active indicator both use the canonical demo.
  const currentDatasource = navigationDatasources.find(
    (ds) => ds.id === navState.currentDatasourceId
  );

  // Toggle collapsed state
  const toggleCollapsed = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem("nav-collapsed", JSON.stringify(newState));
  };

  // Expand nav when clicking on items in collapsed mode. Also close the
  // phone overlay: a tap on the page you are already on does not change
  // `location.pathname`, so the route effect never fires and the sheet stays.
  const handleItemClick = () => {
    if (isCollapsed) {
      setIsCollapsed(false);
      localStorage.setItem("nav-collapsed", "false");
    }
    setIsMobileMenuOpen(false);
  };

  const leaveSession = () => {
    setIsMobileMenuOpen(false);
    logout();
    navigate("/auth");
  };

  // Collection and dashboard links do not call handleItemClick. Any anchor
  // in the sheet is a navigation click, including the current table.
  const closeMobileOnAnchor = (event: React.MouseEvent) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest("a")) setIsMobileMenuOpen(false);
  };

  // Listen for mobile menu toggle event
  useEffect(() => {
    const handleToggleMobileMenu = () => {
      setIsMobileMenuOpen((prev) => !prev);
    };

    window.addEventListener("toggle-mobile-menu", handleToggleMobileMenu);
    return () => {
      window.removeEventListener("toggle-mobile-menu", handleToggleMobileMenu);
    };
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const getDisplayConnectionString = (datasource: DatasourceDto): string => {
    try {
      switch (datasource.type) {
        case DatasourceDtoTypeEnum.POSTGRESQL:
        case DatasourceDtoTypeEnum.MYSQL:
        case DatasourceDtoTypeEnum.MONGODB: {
          const url = new URL(datasource.connectionString);
          return `${url.hostname}:${url.port}`;
        }
        case DatasourceDtoTypeEnum.GOOGLE_SHEETS: {
          const matches = datasource.connectionString.match(
            /^https:\/\/(docs\.google\.com\/spreadsheets\/d\/[a-zA-Z0-9-_]+)/
          );
          return matches ? matches[1] + "..." : datasource.connectionString;
        }
        default:
          return "";
      }
    } catch {
      return "";
    }
  };

  const getLogoForType = (type: string) => {
    switch (type) {
      case "POSTGRESQL":
        return "/logos/postgresql.svg";
      case "MONGODB":
        return "/logos/mongodb.svg";
      case "GOOGLE_SHEETS":
        return "/logos/google-sheets.svg";
      case "MYSQL":
        return "/logos/mysql.svg";
      default:
        return "/logos/database.svg";
    }
  };

  const datasourceHome = (datasource: DatasourceDto) => {
    const name = latestSuccessCollection(datasource.analyzeAttempts);
    if (datasource.id && name) {
      return `/datasources/${datasource.id}/collections/${encodeURIComponent(name)}`;
    }
    return "/data-sources";
  };

  // While a preview is open, Home stays on that member's first tab.
  const previewHome = (datasource: DatasourceDto) => {
    const previewingThis =
      !!urlPreviewId && pathDatasourceId === datasource.id;
    if (!previewingThis) return datasourceHome(datasource);
    const ready =
      preview.memberId === urlPreviewId &&
      preview.datasourceId === datasource.id
        ? preview.view
        : null;
    const first = ready?.tabs[0]?.tab;
    if (first && datasource.id) {
      return hrefWithPreview(
        `/datasources/${datasource.id}/collections/${encodeURIComponent(first)}`,
        urlPreviewId,
      );
    }
    return hrefWithPreview(datasourceHome(datasource), urlPreviewId);
  };

  const getCollectionHeader = (type: string) => {
    switch (type) {
      case "POSTGRESQL":
      case "MYSQL":
        return "Tables";
      case "GOOGLE_SHEETS":
        return "Sheets";
      case "MONGODB":
        return "Collections";
      default:
        return "Collections";
    }
  };

  // Get datasources with collections
  const datasourcesWithCollections = navigationDatasources.filter(
    (ds) => latestSuccessCollection(ds.analyzeAttempts)
  );

  // On Data Sources with a single source, the card already names it in full.
  // A truncated rail copy of the same label is just duplicate chrome.
  const hideSoloDatasourceLabel =
    datasourcesWithCollections.length === 1 && navState.isDataSourcesPage;

  return (
    // The rail is not a scroll container: the collapse control below sits
    // outside its right edge, and `overflow-y` on the rail turned the x axis
    // into a scrollport too, which is where the stray horizontal scrollbar
    // came from. The table list owns vertical scrolling instead.
    <aside
      data-testid="app-sidebar"
      onClick={closeMobileOnAnchor}
      className={`od-nav ${
        isMobileMenuOpen ? "fixed inset-0 z-50 flex" : "hidden md:flex sticky top-0"
      } ${
        isCollapsed ? "w-20" : "w-full md:w-64 lg:w-72"
      } bg-od-surface h-screen border-r border-od-border flex-col group`}
    >
      <Button
        onClick={toggleCollapsed}
        variant="ghost" size="icon" className="absolute -right-4 top-1/2 z-[100] hidden -translate-y-1/2 rounded-full border border-od-border bg-od-surface opacity-0 transition-opacity duration-200 motion-reduce:transition-none hover:bg-od-bg group-hover:opacity-100 focus-visible:opacity-100 [@media(pointer:coarse)]:opacity-100 md:flex"
        title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        style={{ pointerEvents: "auto" }}
      >
        {isCollapsed ? (
          <ChevronRightIcon sx={{ fontSize: 12 }} />
        ) : (
          <ChevronLeftIcon sx={{ fontSize: 12 }} />
        )}
      </Button>

      {/* Logo and Controls */}
      <div
        className={`flex items-center ${
          isCollapsed ? "justify-center" : "justify-between"
        } p-2.5 border-b border-od-border flex-shrink-0`}
      >
        <Link
          to="/"
          aria-label="OnlyDash home"
          className="flex items-center gap-1.5"
        >
          {/* The wordmark is a link, not a heading: each page owns its own h1. */}
          <span
            aria-hidden="true"
            className="text-xl font-bold tracking-tight text-od-text"
          >
            {isCollapsed ? (
              "O"
            ) : (
              <>
                onlydash<span className="text-od-primary">.</span>
              </>
            )}
          </span>
          <span
            className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-semibold tracking-wide bg-od-primary/15 text-od-primary-text border border-od-primary/30"
            data-testid="beta-label"
          >
            Beta
          </span>
        </Link>
        <div className="flex items-center gap-2">
          {!isCollapsed && (
            <Button
              variant="ghost" size="icon"
              title={`Switch to ${
                resolvedMode === "dark" ? "light" : "dark"
              } mode`}
              onClick={toggleMode}
            >
              {resolvedMode === "dark" ? (
                <LightModeIcon sx={{ fontSize: 16 }} />
              ) : (
                <DarkModeIcon sx={{ fontSize: 16 }} />
              )}
            </Button>
          )}
          <Button
            variant="ghost" size="icon" className="md:hidden"
            aria-label="Close menu"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </Button>
        </div>
      </div>

      {/* Header with User Info */}
      <header className="bg-od-surface border-b border-od-border flex-shrink-0">
        {!isCollapsed ? (
          <div className="px-3 py-2.5 flex flex-col gap-1.5">
            <div className="flex items-center gap-2 min-w-0">
              {showGuestChip ? (
                <span
                  className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-semibold tracking-wide bg-od-primary/15 text-od-primary-text border border-od-primary/30"
                  title="Guest session"
                >
                  Guest
                </span>
              ) : (
                <span
                  className="min-w-0 text-xs font-medium text-od-text truncate"
                  title={accountTitle}
                >
                  {accountLabel}
                  {accountName && (
                    <span className="text-xs text-od-muted ml-1">
                      ({accountName})
                    </span>
                  )}
                </span>
              )}
            </div>
            <div className="flex items-center justify-end gap-1.5">
              {!user?.email && !navState.isSettingsPage && (
                <Button
                  onClick={leaveSession}
                  variant="secondary" size="sm"
                  title="Register to save your work"
                >
                  Register
                </Button>
              )}
              <Button
                onClick={leaveSession}
                variant="secondary" size="sm"
                title="Sign out"
              >
                Sign out
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center py-3 gap-2">
            {/* User avatar */}
            <div
              className="w-10 h-10 rounded-full bg-od-surface-2 flex items-center justify-center text-od-primary-text font-semibold text-sm"
              title={user?.email || guestLabel || "Guest"}
            >
              {user?.firstName?.charAt(0) ||
                user?.email?.charAt(0)?.toUpperCase() ||
                "G"}
            </div>
            {showGuestChip && (
              <span
                className="inline-flex items-center px-1 py-0 rounded-full text-[8px] font-semibold tracking-wide bg-od-primary/15 text-od-primary-text border border-od-primary/30"
                title="Guest session"
              >
                Guest
              </span>
            )}
            {/* Action buttons */}
            <div className="flex flex-col gap-1.5 w-full px-2">
              {!user?.email && !navState.isSettingsPage && (
                <Button
                  onClick={leaveSession}
                  variant="secondary" size="sm" className="w-full px-0"
                  title="Register to save your work"
                >
                  <PersonAddAlt1Icon sx={{ fontSize: 13 }} />
                </Button>
              )}
              <Button
                onClick={leaveSession}
                variant="secondary" size="sm" className="w-full px-0"
                title="Sign out"
              >
                <LogoutIcon sx={{ fontSize: 13 }} />
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Data Sources and Settings stay put. The table list below scrolls the
          active row into view; if these two lived in that scroller a 43-table
          schema pushed them above the phone sheet (measured scrollTop 279,
          Settings y=-99, nav clientHeight 714). */}
      <div
        data-testid="nav-chrome"
        className={`${
          isCollapsed ? "px-2 pt-2.5" : "px-2.5 pt-2.5"
        } flex-shrink-0`}
      >
        <div className={`${isCollapsed ? "space-y-2" : "space-y-1"} mb-2`}>
          {/* Data Sources Link */}
          <Link
            to="/data-sources"
            className={`flex items-center rounded-md transition-all duration-200 group relative ${
              isCollapsed ? "flex-col py-3 gap-1 justify-center" : "px-2.5 py-2"
            } ${
              navState.isDataSourcesPage
                ? "bg-od-surface-2 text-od-primary-text border border-od-primary font-semibold"
                : "text-od-muted hover:text-od-text hover:bg-[color-mix(in_srgb,var(--od-muted)_5%,transparent)] font-medium"
            }`}
            title="Data Sources"
            onClick={handleItemClick}
          >
            {navState.isDataSourcesPage && (
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-4 bg-od-primary rounded-r"></div>
            )}
            <StorageIcon
              sx={{ fontSize: isCollapsed ? 18 : 14 }}
              className={`${isCollapsed ? "" : "mr-2.5"} ${
                navState.isDataSourcesPage ? "text-od-primary-text" : "text-od-accent"
              }`}
            />
            {!isCollapsed ? (
              <span className="text-sm">Data Sources</span>
            ) : (
              <span className="text-[11px] font-medium">Data</span>
            )}
          </Link>

          {/* Settings Link */}
          <Link
            to="/settings"
            className={`flex items-center rounded-md transition-all duration-200 group relative ${
              isCollapsed ? "flex-col py-3 gap-1 justify-center" : "px-2.5 py-2"
            } ${
              navState.isSettingsPage
                ? "bg-od-surface-2 text-od-primary-text border border-od-primary font-semibold"
                : "text-od-muted hover:text-od-text hover:bg-[color-mix(in_srgb,var(--od-muted)_5%,transparent)] font-medium"
            }`}
            title="Settings"
            onClick={handleItemClick}
          >
            {navState.isSettingsPage && (
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-4 bg-od-primary rounded-r"></div>
            )}
            <SettingsIcon
              sx={{ fontSize: isCollapsed ? 18 : 14 }}
              className={`text-od-primary-text ${isCollapsed ? "" : "mr-2.5"}`}
            />
            {!isCollapsed ? (
              <span className="text-sm">Settings</span>
            ) : (
              <span className="text-[11px] font-medium">Settings</span>
            )}
          </Link>
        </div>
      </div>

      <nav
        data-testid="nav-collections-scroll"
        className={`${
          isCollapsed ? "px-2 pb-2.5" : "px-2.5 pb-2.5"
        } overflow-y-auto overflow-x-hidden flex-1 min-h-0`}
      >
        {isCollapsed &&
          (currentDatasource ||
            navState.currentCollectionName ||
            navState.isDataSourcesPage ||
            navState.isSettingsPage) && (
            <div className="border-y border-od-border py-3 mb-3 bg-od-bg/30">
              <div className="text-[11px] font-medium text-od-muted text-center mb-3 flex items-center justify-center gap-1">
                Active
              </div>
              <div className="flex flex-col items-center gap-2 px-1">
                {currentDatasource && navState.currentDatasourceId ? (
                  <>
                    {/* Current datasource */}
                    <Link
                      to={previewHome(currentDatasource)}
                      className="flex flex-col items-center gap-1 py-2.5 px-1 bg-od-surface-2 rounded-md border border-od-primary w-full hover:bg-od-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-od-primary"
                      onClick={handleItemClick}
                      title={`Datasource: ${currentDatasource.name}`}
                    >
                      <img
                        src={getLogoForType(currentDatasource.type)}
                        alt={`${currentDatasource.type} logo`}
                        className="w-7 h-7 flex-shrink-0"
                      />
                      {/* The rail is 80 px wide here. Let the ellipsis land
                          where the box ends rather than at a fixed eighth
                          character, and keep the full name on the tile title. */}
                      <span className="text-[11px] font-semibold text-od-primary-text text-center leading-tight w-full px-0.5 overflow-hidden text-ellipsis whitespace-nowrap">
                        {currentDatasource.name}
                      </span>
                    </Link>

                    {/* Current collection */}
                    {navState.currentCollectionName && (
                      <Button
                        type="button"
                        className="flex flex-col items-center gap-1 py-2 px-1 bg-od-surface-2 rounded-md border border-od-border w-full cursor-pointer hover:bg-od-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-od-primary"
                        onClick={handleItemClick}
                        title={`Collection: ${navState.currentCollectionName}`}
                      >
                        <TableRowsIcon
                          sx={{ fontSize: 16 }}
                          className="text-od-primary-text"
                        />
                        <span
                          className="text-[11px] font-semibold text-od-primary-text text-center leading-tight w-full px-0.5 overflow-hidden text-ellipsis break-all"
                          style={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {navState.currentCollectionName}
                        </span>
                      </Button>
                    )}
                  </>
                ) : navState.isDataSourcesPage ? (
                  <div
                    className="flex flex-col items-center gap-1 py-2.5 px-1 bg-od-surface-2 rounded-md border border-od-primary w-full"
                    title="Data Sources"
                  >
                    <StorageIcon
                      sx={{ fontSize: 18 }}
                      className="text-od-primary-text"
                    />
                    <span className="text-[11px] font-semibold text-od-primary-text text-center leading-tight">
                      Data
                    </span>
                  </div>
                ) : navState.isSettingsPage ? (
                  <div
                    className="flex flex-col items-center gap-1 py-2.5 px-1 bg-od-surface-2 rounded-md border border-od-primary w-full"
                    title="Settings"
                  >
                    <SettingsIcon
                      sx={{ fontSize: 18 }}
                      className="text-od-primary-text"
                    />
                    <span className="text-[11px] font-semibold text-od-primary-text text-center leading-tight">
                      Settings
                    </span>
                  </div>
                ) : null}
              </div>
            </div>
          )}

        {/* All Sources header for collapsed mode */}
        {!hideSoloDatasourceLabel &&
          isCollapsed &&
          datasourcesWithCollections.length > 0 && (
          <div className="border-t border-od-border pt-2 mb-2">
            <div className="text-[8px] font-bold text-od-muted uppercase tracking-wider text-center mb-2">
              All Sources
            </div>
          </div>
        )}

        {/* Datasources List */}
        {!hideSoloDatasourceLabel &&
          datasourcesWithCollections.map((datasource) => {
          const isDatasourceActive = navState.currentDatasourceId === datasource.id;
          const success = latestSuccessAttempt(datasource.analyzeAttempts);
          const allDashboards = success?.uiGenerationAttempts?.[0]?.panels?.flatMap(
            (p) => p?.dashboards ?? []
          ) || [];
          const schemaCollections = success?.databaseCollections || [];
          const previewingThis =
            !!urlPreviewId && pathDatasourceId === datasource.id;
          const previewView =
            previewingThis &&
            preview.memberId === urlPreviewId &&
            preview.datasourceId === datasource.id
              ? preview.view
              : null;
          const collections = previewView
            ? collectionsFromPreview(schemaCollections, previewView.tabs)
            : previewingThis
              ? []
              : visibleCollections(
                  schemaCollections,
                  (datasource as { access?: string; viewerRole?: string }).access ??
                    (datasource as { viewerRole?: string }).viewerRole,
                  (datasource as { tabRules?: { tab: string; level: "NONE" | "VIEW" | "EDIT" }[] })
                    .tabRules,
                );
          const homePath = previewHome(datasource);
          // One anchor per entity. The generator emits one page per entity and
          // the collection route now draws that entity's charts as well as its
          // grid, so a single-entity dashboard link was a second door onto the
          // same room: same table, same figures, opposite order, nothing in the
          // label saying which is which. Multi-entity pages, and pages naming a
          // collection the analyse did not return, have no equivalent route and
          // keep their link.
          const dashboards = navDashboards(
            allDashboards,
            collections.map((collection) => collection.name as string),
          );
          const connectionLabel = getDisplayConnectionString(datasource)?.trim();

          return (
            <div key={datasource.id} className={isCollapsed ? "mb-2" : "mb-3"}>
              {/* Datasource Header */}
              {!isCollapsed ? (
                <div
                  className={`rounded-md p-2.5 border transition-all ${
                    isDatasourceActive
                      ? "bg-od-surface-2 border-od-primary"
                      : "bg-od-surface border-od-border"
                  }`}
                >
                  {/* The name owns the whole heading line. Sharing it with the
                      provider pill cut a 19-character source name down to
                      "OnlyDash te…" on a 288 px rail. */}
                  <h2 className="text-sm font-semibold text-od-text flex items-center min-w-0">
                    {isDatasourceActive && (
                      <div className="w-1 h-4 bg-od-primary rounded-r mr-1.5 flex-shrink-0"></div>
                    )}
                    <Link to={homePath} className="inline-flex items-center justify-center flex-shrink-0 mr-2">
                    <img
                      src={getLogoForType(datasource.type)}
                      alt={`${datasource.type} logo`}
                      className="w-4 h-4"
                    />
                    </Link>
                    <span
                      className={`truncate ${
                        isDatasourceActive ? "text-od-primary-text font-bold" : ""
                      }`}
                      title={datasourceDisplayName(datasource)}
                    >
                      {datasourceDisplayName(datasource)}
                    </span>
                  </h2>
                  <div className="mt-1 flex items-center gap-1.5 min-w-0">
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 whitespace-nowrap ${
                        isDatasourceActive
                          ? "bg-od-primary/20 text-od-primary-text border border-od-primary/50"
                          : "bg-[color-mix(in_srgb,var(--od-muted)_5%,transparent)] text-od-muted border border-od-border"
                      }`}
                    >
                      {datasource.type.replace("_", " ").toLowerCase()}
                    </span>
                    {connectionLabel && (
                      <span
                        className="text-od-muted text-xs truncate"
                        title={connectionLabel}
                      >
                        {connectionLabel}
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <Link
                  to={homePath}
                  className={`flex flex-col items-center gap-1 py-2 px-1 bg-od-surface rounded-md border transition-colors group w-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-od-primary ${
                    isDatasourceActive
                      ? "border-od-primary bg-od-surface-2"
                      : "border-od-border hover:border-od-primary"
                  }`}
                  title={datasourceDisplayName(datasource)}
                  onClick={handleItemClick}
                >
                  <img
                    src={getLogoForType(datasource.type)}
                    alt={`${datasource.type} logo`}
                    className="w-7 h-7 flex-shrink-0"
                  />
                  <span className="text-[11px] font-medium text-od-muted text-center leading-tight w-full px-0.5 overflow-hidden text-ellipsis whitespace-nowrap">
                    {datasourceDisplayName(datasource)}
                  </span>
                  {collections.length > 0 && (
                    <span className="text-[11px] px-1.5 py-0.5 bg-od-primary/20 text-od-primary-text rounded-full font-semibold">
                      {collections.length}
                    </span>
                  )}
                </Link>
              )}

              {/* Generated UI: collection routes only. /data-sources is
                  sources; the card already names tables as chips. */}
              {!isCollapsed &&
                !navState.isDataSourcesPage &&
                !navState.isSettingsPage &&
                dashboards.length > 0 &&
                !previewingThis && (
                <div className="mt-2 ml-1.5">
                  <h3 className="text-xs font-semibold text-od-muted px-2 mb-1 uppercase tracking-wider">
                    Generated UI
                  </h3>
                  <div className="space-y-1">
                    {dashboards.map((dashboard) => {
                      const isDashboardActive =
                        navState.currentDashboardId === dashboard.id &&
                        navState.currentDatasourceId === datasource.id;
                      return (
                        <Link
                          key={dashboard.id}
                          to={`/datasources/${datasource.id}/dashboards/${dashboard.id}`}
                          className={`flex items-center px-2.5 py-1.5 rounded-md text-xs transition-all duration-200 group relative ${
                            isDashboardActive
                              ? "bg-od-surface-2 text-od-primary-text border border-od-primary font-semibold"
                              : "text-od-muted hover:text-od-text hover:bg-[color-mix(in_srgb,var(--od-muted)_5%,transparent)]"
                          }`}
                        >
                          {isDashboardActive && (
                            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-4 bg-od-primary rounded-r"></div>
                          )}
                          <SpeedIcon
                            sx={{ fontSize: 13 }}
                            className={`mr-2 ${
                              isDashboardActive
                                ? "text-od-primary-text"
                                : "text-od-primary-text group-hover:text-od-accent"
                            }`}
                          />
                          <span className="truncate" title={dashboard.name}>
                            {dashboard.name}
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Collections/Tables */}
              {!isCollapsed &&
                !navState.isDataSourcesPage &&
                !navState.isSettingsPage && (
                <CollectionList
                  heading={getCollectionHeader(datasource.type) ?? "Tables"}
                  datasourceId={datasource.id!}
                  collections={collections}
                  previewMemberId={previewView ? urlPreviewId : null}
                  activeCollectionName={
                    navState.currentDatasourceId === datasource.id
                      ? navState.currentCollectionName
                      : undefined
                  }
                />
              )}
            </div>
          );
        })}
      </nav>
    </aside>
  );
};

export default Nav;
